import { useEffect, useRef, useState, type ReactElement } from 'react';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * App-wide file drop target. Listens for a file drag over the whole window and, on drop, hands the
 * dropped files to the host (which uploads them and attaches them to the current note). It renders a
 * full-window "drop to attach" affordance while a drag is in flight so the target is discoverable
 * anywhere, not just inside the editor.
 *
 * Two things it deliberately does NOT do:
 *  - It never handles a drop that lands inside an editor (`.ProseMirror`) or any element that opts out
 *    via `[data-lapo-file-drop-ignore]` (the assistant composer): those own their own drop handling, so
 *    forwarding here would double-insert. We simply let their handlers run.
 *  - The overlay itself is `pointer-events: none` (visual only). All the drag/drop logic lives on window
 *    listeners, so a drop over the editor still reaches the editor — the overlay never intercepts it.
 *
 * It also fixes a latent browser behavior: without a window-level `dragover`/`drop` preventDefault, a
 * file dropped onto any non-editor area makes the browser navigate to (open/download) the file. Here we
 * always preventDefault for file drags, so an errant drop is a no-op instead of blowing away the app.
 */
export interface FileDropOverlayProps {
  /** Handle files dropped anywhere outside an editor / opt-out zone. */
  onFiles: (files: File[]) => void;
  /**
   * When set, dropped files are ignored entirely (uploads not configured / offline). We still swallow
   * the drop so the browser doesn't open the file, but no overlay is shown and `onFiles` never fires.
   */
  disabled?: boolean;
  /** Overlay heading (default: "Drop to attach"). */
  label?: string;
  /** Overlay sub-line (default: "Images embed inline · other files attach as a link"). */
  hint?: string;
}

/** A drag carries files (not text/nodes being reordered) when its dataTransfer advertises the Files type. */
function dragHasFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  // `types` is a DOMStringList in some engines and a plain array in others — normalize before checking.
  return Array.from(types as ArrayLike<string>).includes('Files');
}

/** True when the event happened inside an editor or an element that opts out of app-wide drop handling. */
function isOwnHandledTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('.ProseMirror, [data-lapo-file-drop-ignore]');
}

export function FileDropOverlay({ onFiles, disabled, label, hint }: FileDropOverlayProps): ReactElement | null {
  const [active, setActive] = useState(false);
  // dragenter/dragleave fire for every child element the cursor crosses, so a naive boolean flickers.
  // Counting enters minus leaves tells us when the drag has genuinely left the window (depth back to 0).
  const depth = useRef(0);

  useEffect(() => {
    const reset = (): void => {
      depth.current = 0;
      setActive(false);
    };
    const onEnter = (e: DragEvent): void => {
      if (!dragHasFiles(e)) return;
      depth.current += 1;
      if (!disabled && !isOwnHandledTarget(e.target)) setActive(true);
    };
    const onOver = (e: DragEvent): void => {
      if (!dragHasFiles(e)) return;
      // Required to permit a drop AND to stop the browser's default "open the dropped file" behavior.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onLeave = (e: DragEvent): void => {
      if (!dragHasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setActive(false);
    };
    const onDrop = (e: DragEvent): void => {
      if (!dragHasFiles(e)) return;
      reset();
      // Always swallow a file drop so the browser never navigates to (opens) the file — this holds even
      // over the editor (which already preventDefault'd in its own handler) and over opt-out zones.
      e.preventDefault();
      // Editors and opt-out zones (assistant composer) own their drops — don't double-handle.
      if (isOwnHandledTarget(e.target) || disabled) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    };
    // A drag that ends outside the window (dropped on the desktop, Escape) never fires `drop`; dragend
    // clears the overlay so it can't get stuck on.
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', reset);
    };
  }, [onFiles, disabled]);

  if (!active) return null;
  return (
    <div className="lapo-file-drop-overlay is-active" aria-hidden>
      <div className="lapo-file-drop-card">
        <MaterialIcon name="upload_file" size={34} />
        <p className="lapo-file-drop-title">{label ?? 'Drop to attach'}</p>
        <p className="lapo-file-drop-hint">{hint ?? 'Images embed inline · other files attach as a link'}</p>
      </div>
    </div>
  );
}
